import { useEffect, useMemo, useRef, useState } from 'react'
import { useLanguage } from '../contexts/LanguageContext'
import { btnAccentSm, btnSecondarySm } from '../lib/ui'

interface Props {
  /** yyyy-mm-dd。未選択は空文字 */
  from: string
  to: string
  onChange: (from: string, to: string) => void
  className?: string
}

const pad = (n: number) => String(n).padStart(2, '0')

/** Date → yyyy-mm-dd。toISOString は UTC にずれるのでローカルの年月日から組む */
const toKey = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`

const parseKey = (s: string): Date | null => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s)
  if (!m) return null
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
  return Number.isNaN(d.getTime()) ? null : d
}

const startOfToday = () => {
  const d = new Date()
  d.setHours(0, 0, 0, 0)
  return d
}

const addMonths = (d: Date, n: number) => new Date(d.getFullYear(), d.getMonth() + n, 1)

/** その月のカレンダーマス（日曜始まり。前後の余白は null） */
const monthCells = (base: Date): (Date | null)[] => {
  const year = base.getFullYear()
  const month = base.getMonth()
  const first = new Date(year, month, 1)
  const daysInMonth = new Date(year, month + 1, 0).getDate()
  const cells: (Date | null)[] = Array.from({ length: first.getDay() }, () => null)
  for (let day = 1; day <= daysInMonth; day++) cells.push(new Date(year, month, day))
  while (cells.length % 7 !== 0) cells.push(null)
  return cells
}

export function DateRangePicker({ from, to, onChange, className }: Props) {
  const { t, lang } = useLanguage()
  const locale = lang === 'ja' ? 'ja-JP' : 'en-US'
  const today = useMemo(startOfToday, [])

  const [open, setOpen] = useState(false)
  // 1回目のクリックで開始日、2回目で終了日。確定するまで親には渡さない
  const [pendingStart, setPendingStart] = useState<string | null>(null)
  const [hovered, setHovered] = useState<string | null>(null)
  const [leftMonth, setLeftMonth] = useState(() => {
    const anchor = parseKey(from) ?? parseKey(to) ?? today
    return new Date(anchor.getFullYear(), anchor.getMonth(), 1)
  })

  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onPointerDown = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false)
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  // 閉じたら選択途中の状態は破棄する（確定済みの期間だけを残す）
  useEffect(() => {
    if (open) return
    setPendingStart(null)
    setHovered(null)
  }, [open])

  const openPicker = () => {
    const anchor = parseKey(from) ?? parseKey(to) ?? today
    setLeftMonth(new Date(anchor.getFullYear(), anchor.getMonth(), 1))
    setOpen(true)
  }

  const commit = (nextFrom: string, nextTo: string) => {
    setPendingStart(null)
    setHovered(null)
    setOpen(false)
    if (nextFrom !== from || nextTo !== to) onChange(nextFrom, nextTo)
  }

  const handleDayClick = (key: string) => {
    if (!pendingStart) {
      setPendingStart(key)
      setHovered(null)
      return
    }
    // 開始日より前を選んだら、そこを新しい開始日として選び直す
    if (key < pendingStart) {
      setPendingStart(key)
      return
    }
    commit(pendingStart, key)
  }

  const weekdayLabels = useMemo(() => {
    // 2024-09-01 は日曜。そこから 7 日分でロケール順のラベルを作る
    const fmt = new Intl.DateTimeFormat(locale, { weekday: 'narrow' })
    return Array.from({ length: 7 }, (_, i) => fmt.format(new Date(2024, 8, 1 + i)))
  }, [locale])

  const monthLabel = (d: Date) =>
    d.toLocaleDateString(locale, { year: 'numeric', month: 'long' })

  const displayDate = (key: string) => {
    const d = parseKey(key)
    return d ? d.toLocaleDateString(locale, { year: 'numeric', month: 'short', day: 'numeric' }) : ''
  }

  const todayKey = toKey(today)
  const rangeStart = pendingStart ?? (from || null)
  const rangeEnd = pendingStart ? (hovered && hovered >= pendingStart ? hovered : null) : (to || null)

  const presets: { label: string; range: () => [string, string] }[] = [
    {
      label: t('date_range_this_month'),
      range: () => [toKey(new Date(today.getFullYear(), today.getMonth(), 1)), todayKey],
    },
    {
      label: t('date_range_last_month'),
      range: () => [
        toKey(new Date(today.getFullYear(), today.getMonth() - 1, 1)),
        toKey(new Date(today.getFullYear(), today.getMonth(), 0)),
      ],
    },
    {
      label: t('date_range_this_year'),
      range: () => [toKey(new Date(today.getFullYear(), 0, 1)), todayKey],
    },
    {
      label: t('date_range_last_30_days'),
      range: () => [
        toKey(new Date(today.getFullYear(), today.getMonth(), today.getDate() - 29)),
        todayKey,
      ],
    },
  ]

  const triggerLabel = from || to
    ? `${from ? displayDate(from) : '…'} – ${to ? displayDate(to) : '…'}`
    : t('date_range_placeholder')

  // 未来月を見ても選べる日が無いので、右側の月が今月に達したら次へ進めない
  const rightMonth = addMonths(leftMonth, 1)
  const canGoNext = rightMonth.getFullYear() < today.getFullYear()
    || (rightMonth.getFullYear() === today.getFullYear() && rightMonth.getMonth() < today.getMonth())

  const renderMonth = (base: Date, extraClass = '') => (
    <div className={extraClass}>
      <div className="mb-2 text-center text-sm font-semibold text-gray-900 dark:text-gray-100">
        {monthLabel(base)}
      </div>
      <div className="grid grid-cols-7">
        {weekdayLabels.map((label, i) => (
          <div key={i} className="pb-1 text-center text-xs font-medium text-gray-400 dark:text-gray-500">
            {label}
          </div>
        ))}
        {monthCells(base).map((day, i) => {
          if (!day) return <div key={i} className="h-9" />
          const key = toKey(day)
          const disabled = key > todayKey
          const isStart = !!rangeStart && key === rangeStart
          const isEnd = !!rangeEnd && key === rangeEnd
          const inRange = !!rangeStart && !!rangeEnd && key > rangeStart && key < rangeEnd
          const isEdge = isStart || isEnd
          const hasBand = !!rangeStart && !!rangeEnd && (inRange || isEdge)

          let bandClass = ''
          if (hasBand && !(isStart && isEnd)) {
            bandClass = 'bg-indigo-50 dark:bg-indigo-950'
            if (isStart) bandClass += ' rounded-l-lg'
            if (isEnd) bandClass += ' rounded-r-lg'
          }

          let dayClass = 'text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700'
          if (disabled) {
            dayClass = 'text-gray-300 cursor-not-allowed dark:text-gray-600'
          } else if (isEdge) {
            dayClass = 'bg-indigo-600 text-white font-semibold hover:bg-indigo-700'
          } else if (inRange) {
            dayClass = 'text-indigo-900 hover:bg-indigo-100 dark:text-indigo-200 dark:hover:bg-indigo-900'
          }

          return (
            <div key={i} className={bandClass}>
              <button
                type="button"
                disabled={disabled}
                onClick={() => handleDayClick(key)}
                onMouseEnter={() => { if (!disabled) setHovered(key) }}
                aria-label={displayDate(key)}
                aria-pressed={isEdge}
                className={`h-9 w-full rounded-lg text-sm transition-colors ${dayClass} ${
                  key === todayKey && !isEdge ? 'ring-1 ring-inset ring-indigo-300 dark:ring-indigo-700' : ''
                }`}
              >
                {day.getDate()}
              </button>
            </div>
          )
        })}
      </div>
    </div>
  )

  return (
    <div ref={containerRef} className={`relative ${className ?? ''}`}>
      <button
        type="button"
        onClick={() => (open ? setOpen(false) : openPicker())}
        aria-haspopup="dialog"
        aria-expanded={open}
        className={`flex w-full items-center gap-2 rounded-lg border px-3 py-2 text-left text-sm transition-colors dark:bg-gray-800 dark:text-gray-100 ${
          open
            ? 'border-indigo-500 ring-1 ring-indigo-500 dark:border-indigo-500'
            : 'border-gray-200 hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-700'
        }`}
      >
        <span aria-hidden="true" className="text-gray-400 dark:text-gray-500">🗓</span>
        <span className={from || to ? '' : 'text-gray-400 dark:text-gray-500'}>{triggerLabel}</span>
      </button>

      {open && (
        <div
          role="dialog"
          aria-label={t('date_range_placeholder')}
          onMouseLeave={() => setHovered(null)}
          className="absolute left-0 top-full z-30 mt-2 w-[19.5rem] rounded-xl border border-gray-200 bg-white p-4 shadow-lg sm:left-auto sm:right-0 sm:w-[37rem] dark:border-gray-700 dark:bg-gray-900"
        >
          <div className="mb-2 flex items-center justify-between">
            <button
              type="button"
              onClick={() => setLeftMonth((m) => addMonths(m, -1))}
              aria-label={t('date_range_prev_month')}
              className={btnSecondarySm}
            >
              ◀
            </button>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {pendingStart ? t('date_range_pick_end') : t('date_range_pick_start')}
            </span>
            <button
              type="button"
              onClick={() => setLeftMonth((m) => addMonths(m, 1))}
              disabled={!canGoNext}
              aria-label={t('date_range_next_month')}
              className={btnSecondarySm}
            >
              ▶
            </button>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {renderMonth(leftMonth)}
            {/* モバイル幅では 1 ヶ月表示にフォールバックする */}
            {renderMonth(rightMonth, 'hidden sm:block')}
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-gray-100 pt-3 dark:border-gray-800">
            {presets.map((preset) => (
              <button
                key={preset.label}
                type="button"
                onClick={() => {
                  const [f, tt] = preset.range()
                  commit(f, tt)
                }}
                className={btnSecondarySm}
              >
                {preset.label}
              </button>
            ))}
            <button
              type="button"
              onClick={() => commit('', '')}
              disabled={!from && !to && !pendingStart}
              className={`${btnAccentSm} ml-auto`}
            >
              {t('date_range_clear')}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
